import { useId, useState } from 'react';
import type { CSSProperties } from 'react';
import { Icon } from '../core/Icon';

export interface AIComposerProps {
    value?: string;
    onChange?: (valor: string) => void;
    onSend?: (valor: string) => void;
    placeholder?: string;
    /** Nome acessível do campo. O placeholder some ao digitar e não serve de rótulo. */
    ariaLabel?: string;
    suggestions?: string[];
    disclaimer?: string;
    /** Sem manipulador, o botão de anexar não é renderizado. */
    onAttach?: () => void;
    /** Sem manipulador, o botão de ditar não é renderizado. */
    onDictate?: () => void;
    style?: CSSProperties;
}

export function AIComposer({
    value = '', onChange, onSend, placeholder = 'Pergunte qualquer coisa',
    ariaLabel = 'Pergunte à assistente VecchIA', suggestions = [],
    disclaimer = 'A VecchIA pode cometer erros. Confira informações importantes.',
    onAttach, onDictate, style,
}: AIComposerProps) {
    const [focus, setFocus] = useState(false);
    const idDica = useId();

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, ...style }}>
            {suggestions.length > 0 && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {suggestions.map((s) => (
                        <button
                            key={s} type="button" onClick={() => onChange?.(s)}
                            style={{ height: 26, padding: '0 10px', borderRadius: 'var(--radius-pill)', border: '1px solid var(--border)', background: 'var(--surface)', cursor: 'pointer', fontFamily: 'var(--font-ui)', fontSize: 12.5, color: 'var(--neutral-700)' }}
                        >
                            {s}
                        </button>
                    ))}
                </div>
            )}
            <div
                style={{
                    border: `1px solid ${focus ? 'var(--burgundy-300)' : 'var(--border)'}`, borderRadius: 'var(--radius-xl)',
                    background: 'var(--surface)', padding: 10, display: 'flex', flexDirection: 'column', gap: 8,
                    boxShadow: focus ? 'var(--ai-glow)' : 'var(--shadow-xs)', transition: 'var(--transition-control)',
                }}
            >
                <textarea
                    value={value} placeholder={placeholder} rows={2}
                    aria-label={ariaLabel} aria-describedby={disclaimer ? idDica : undefined}
                    onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
                    onChange={(e) => onChange?.(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            onSend?.(value);
                        }
                    }}
                    style={{ border: 0, outline: 'none', resize: 'none', fontFamily: 'var(--font-ui)', fontSize: 14, lineHeight: '20px', color: 'var(--text-primary)', background: 'transparent' }}
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {/* Anexar e ditar só existem com manipulador: botão que não faz
                        nada é pior que botão ausente — ele promete e falha calado. */}
                    {onAttach && <BotaoRedondo label="Anexar" icon="plus" onClick={onAttach} />}
                    <span style={{ flex: 1 }} />
                    {onDictate && <BotaoRedondo label="Ditar" icon="mic" onClick={onDictate} />}
                    <button
                        type="button" onClick={() => onSend?.(value)} aria-label="Enviar" disabled={!value}
                        style={{ width: 28, height: 28, borderRadius: '50%', border: 0, cursor: value ? 'pointer' : 'not-allowed', background: value ? 'var(--burgundy-700)' : 'var(--neutral-200)', color: value ? '#fff' : 'var(--neutral-500)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                        <Icon name="arrow-up" size={15} />
                    </button>
                </div>
            </div>
            {disclaimer && <p id={idDica} style={{ margin: 0, textAlign: 'center', fontSize: 11.5, color: 'var(--text-tertiary)' }}>{disclaimer}</p>}
        </div>
    );
}

function BotaoRedondo({ label, icon, onClick }: { label: string; icon: string; onClick: () => void }) {
    return (
        <button
            type="button" aria-label={label} title={label} onClick={onClick}
            style={{ width: 28, height: 28, borderRadius: '50%', border: '1px solid var(--border)', background: 'var(--surface)', cursor: 'pointer', color: 'var(--neutral-600)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
            <Icon name={icon} size={icon === 'mic' ? 14 : 15} />
        </button>
    );
}
